import Config from './Config.ts';
import { MappableRestaurant } from './types.ts';

/**
 * Parses a coordinate value (string or number) into a number.
 * @param coord - The coordinate to parse (may be string from API or number)
 * @returns The parsed number, or null if invalid
 */
function parseCoordinate(coord: string | number | null | undefined): number | null {
  if (coord === null || coord === undefined) {
    return null;
  }

  const numCoord = typeof coord === 'string' ? parseFloat(coord) : coord;

  if (typeof numCoord !== 'number' || isNaN(numCoord)) {
    return null;
  }

  return numCoord;
}

/**
 * Generates a Mapbox Static Images API URL for a restaurant location.
 * @param width - Image width in pixels
 * @param height - Image height in pixels
 * @param restaurant - Restaurant with latitude/longitude coordinates
 * @param twoX - Whether to request a 2x retina image
 * @returns URL object for the static map, or null if coordinates/config are invalid
 */
export const getMapUrl = (
  width: number,
  height: number,
  restaurant?: MappableRestaurant,
  twoX = false
): URL | null => {
  if (!restaurant) {
    return null;
  }

  // Parse coordinates (handles both string and number values from API)
  const lat = parseCoordinate(restaurant.latitude);
  const lon = parseCoordinate(restaurant.longitude);

  // Validate coordinates are valid numbers
  if (lat === null || lon === null) {
    if (import.meta.env.DEV) {
      console.warn('Invalid restaurant coordinates for map:', {
        latitude: restaurant.latitude,
        longitude: restaurant.longitude,
      });
    }
    return null;
  }

  const { mapbox } = Config.data;

  // Validate Mapbox configuration
  if (!mapbox.access_token || !mapbox.username || !mapbox.style_id) {
    if (import.meta.env.DEV) {
      console.warn('Missing Mapbox configuration');
    }
    return null;
  }

  const zoom = 15;
  const bearing = 0;
  const _2x = twoX ? '@2x' : '';

  // https://docs.mapbox.com/api/maps/static-images/
  const url = new URL(
    `https://api.mapbox.com/styles/v1/${mapbox.username}/${mapbox.style_id}/static/pin-l-embassy+000(${lon},${lat})/${lon},${lat},${zoom},${bearing}/${width}x${height}${_2x}`
  );
  url.searchParams.append('access_token', mapbox.access_token);

  return url;
};
